import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { List, ListItem, Table } from "@mantine/core";

import range from "lodash/range";
import { Fragment } from "react/jsx-runtime";

import { TextWrapper } from "@/components/TextWrapper";
import ActionCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/ActionCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import TimeCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/TimeCell";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getCervicalExamRowValues } from "@/encounters/components/EncounterLabor/utils/labor";

interface CervicalExamRowProps {
  time: string;
  observation: Observation;
  showNumbersOnly: boolean;
  showPreviousVersions?: boolean;
  isPreviousVersion?: boolean;
  isViewOnly?: boolean;
}

export default function CervicalExamRow({
  time,
  observation,
  showNumbersOnly,
  showPreviousVersions = true,
  isPreviousVersion,
  isViewOnly = false,
}: CervicalExamRowProps) {
  const {
    dilation,
    effacement,
    station,
    consistency,
    location,
    activeLabor,
    fetalPresentation,
    notes,
    hasAnswers,
    hasNotes,
  } = useMemo(() => getCervicalExamRowValues(observation), [observation]);

  const previousVersions = hasPreviousVersions(observation);

  // Determine if details row is needed
  const detailsRowNeeded =
    !showNumbersOnly && (hasAnswers || hasNotes || notes || isPreviousVersion);

  const showTitle = consistency || location || activeLabor || fetalPresentation;

  // Row span for TimeCell and ActionCell
  const rowSpan = hasAnswers && detailsRowNeeded && !showNumbersOnly ? 2 : 1;

  return (
    <Fragment>
      {/* Value row: only if there are answers and not a previous version */}
      {hasAnswers && !isPreviousVersion && showPreviousVersions && (
        <Table.Tr bg="white">
          <TimeCell
            time={time}
            rowSpan={rowSpan}
            isViewOnly={isViewOnly}
            observation={observation}
          />
          {range(7).map((i) => (
            <Table.Td key={i} />
          ))}
          <Table.Td>{dilation}</Table.Td>
          <Table.Td>{effacement}</Table.Td>
          <Table.Td>{station}</Table.Td>
          {!isViewOnly && <ActionCell rowSpan={rowSpan} observation={observation} />}
        </Table.Tr>
      )}
      {/* Details row: if there are notes/details or isPreviousVersion is true */}
      {detailsRowNeeded && (
        <Table.Tr bg="white" className={classes.tableRow}>
          {showPreviousVersions && (!hasAnswers || isPreviousVersion) && (
            <TimeCell
              time={time}
              rowSpan={rowSpan}
              isViewOnly={isViewOnly}
              observation={observation}
            />
          )}
          <Table.Td p="md" colSpan={10}>
            <>
              {showTitle && <TextWrapper size="sm">Cervical Exam:</TextWrapper>}
              <List fz="sm" withPadding>
                {consistency && (
                  <ListItem>
                    <strong>Consistency:</strong> {consistency}
                  </ListItem>
                )}
                {location && (
                  <ListItem>
                    <strong>Location of Cervix:</strong> {location}
                  </ListItem>
                )}
                {activeLabor && (
                  <ListItem>
                    <strong>Active Labor:</strong> {activeLabor}
                  </ListItem>
                )}
                {fetalPresentation && (
                  <ListItem>
                    <strong>Fetal Presentation:</strong> {fetalPresentation}
                  </ListItem>
                )}
                {isPreviousVersion && (
                  <>
                    {dilation && (
                      <ListItem>
                        <strong>Dilation:</strong> {dilation}
                      </ListItem>
                    )}
                    {effacement && (
                      <ListItem>
                        <strong>Effacement:</strong> {effacement}
                      </ListItem>
                    )}
                    {station && (
                      <ListItem>
                        <strong>Station:</strong> {station}
                      </ListItem>
                    )}
                  </>
                )}
              </List>
            </>
            {notes && (
              <TextWrapper pt="xs" size="sm">
                Cervical Exam Comments: {notes}
              </TextWrapper>
            )}
            {showPreviousVersions && previousVersions ? (
              <PreviousVersions
                observation={observation}
                showPreviousVersions={showPreviousVersions}
                rowComponent={(props) => <CervicalExamRow {...props} isPreviousVersion={true} />}
              />
            ) : (
              <LastUpdated observation={observation} showPreviousVersions={false} />
            )}
          </Table.Td>
          {/* Only render ActionCell in details row if value row is not present and not a previous version */}
          {(!hasAnswers || isPreviousVersion) && !isPreviousVersion && !isViewOnly && (
            <ActionCell rowSpan={1} observation={observation} />
          )}
        </Table.Tr>
      )}
    </Fragment>
  );
}
